/**
 * OAuth 2.0 client — Authorization Code + PKCE flow
 *
 * All configuration is read from window.OAuthConfig (injected by Laravel
 * via HomeController.php). No secrets or IDs are hardcoded here.
 *
 * PKCE verifier lifecycle:
 *   buildAuthorizeUrl  → generates + stores verifier
 *   exchangeCodeForTokens → retrieves + clears verifier
 */

import {
  generateCodeVerifier,
  generateCodeChallenge,
  storeVerifier,
  retrieveVerifier,
  clearVerifier,
} from '../utils/pkce';

/**
 * Return the runtime OAuth configuration from window.OAuthConfig.
 * Throws early with a clear message if config is absent — prevents silent failures.
 *
 * @returns {object} OAuthConfig
 * @throws {Error} when window.OAuthConfig is not defined
 */
function getConfig() {
  const config = window.OAuthConfig;
  if (!config) {
    throw new Error('window.OAuthConfig is not defined. Ensure Laravel has injected the OAuth config.');
  }
  return config;
}

/**
 * POST form-encoded data to the Cognito token endpoint.
 *
 * @param {string} tokenEndpoint - Full URL to the token endpoint
 * @param {URLSearchParams} params - Form body parameters
 * @returns {Promise<object>} Parsed JSON response
 * @throws {Error} on non-2xx responses or network failure
 */
async function postToTokenEndpoint(tokenEndpoint, params) {
  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: params.toString(),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(
      `Token endpoint returned ${response.status}: ${errorBody.error || 'unknown error'}`
    );
  }

  return response.json();
}

/**
 * Build the authorization URL.
 *
 * When `OAuthConfig.authUiUrl` is set, redirects to the custom auth-ui SPA
 * instead of Cognito's Hosted UI. The auth-ui handles:
 *   - Email/password: SDK auth → token handoff to /oauth/callback via URL hash
 *   - Google: forwards the PKCE code_challenge to Cognito, code returns here
 *
 * When `authUiUrl` is not set, falls back to Cognito's Hosted UI directly.
 *
 * PKCE is always generated here so the verifier is in KeeperFA's sessionStorage
 * regardless of which path is used — the Google flow via auth-ui needs it.
 *
 * @param {object} options
 * @param {string} [options.prompt]           - e.g. 'login', 'none' (Cognito path only)
 * @param {string} [options.identityProvider] - e.g. 'Google' (Cognito path only)
 * @param {string} [options.state]            - Opaque value roundtripped by Cognito
 * @param {string} [options.loginHint]        - Pre-fills email field on the login UI
 * @returns {Promise<string>} Full authorization URL
 */
export async function buildAuthorizeUrl({ prompt, identityProvider, state, loginHint } = {}) {
  const config = getConfig();
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  storeVerifier(verifier);

  // --- Custom auth-ui path ---
  // When authUiUrl is configured, send the user to the auth-ui SPA with all
  // OAuth params as query string so the auth-ui can complete the flow.
  if (config.authUiUrl) {
    const params = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      scope: config.scopes,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    if (prompt !== undefined && prompt !== null) {
      params.set('prompt', prompt);
    }
    if (state !== undefined && state !== null) {
      params.set('state', state);
    }
    if (loginHint !== undefined && loginHint !== null) {
      params.set('login_hint', loginHint);
    }
    return `${config.authUiUrl}/?${params.toString()}`;
  }

  // --- Cognito Hosted UI path (default) ---
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: config.scopes,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  if (prompt !== undefined && prompt !== null) {
    params.set('prompt', prompt);
  }
  if (identityProvider !== undefined && identityProvider !== null) {
    params.set('identity_provider', identityProvider);
  }
  if (state !== undefined && state !== null) {
    params.set('state', state);
  }

  return `https://${config.authDomain}/oauth2/authorize?${params.toString()}`;
}

/**
 * Exchange an authorization code for tokens (Authorization Code + PKCE grant).
 *
 * Retrieves the stored PKCE verifier, sends it with the code to the token
 * endpoint, then clears the verifier regardless of outcome.
 *
 * @param {string} code - The authorization code from the callback URL
 * @param {string} [redirectUriOverride] - The redirect_uri used in the original
 *   authorization request. Must be passed when auth-ui intercepted the Google
 *   callback (its /google-callback URL was the redirect_uri sent to Cognito).
 * @returns {Promise<{access_token: string, id_token: string, refresh_token: string}>}
 * @throws {Error} when no verifier is stored, or when the token endpoint errors
 */
export async function exchangeCodeForTokens(code, redirectUriOverride) {
  const config = getConfig();
  const verifier = retrieveVerifier();

  if (!verifier) {
    throw new Error(
      'No PKCE code verifier found in sessionStorage. ' +
      'The login flow may have been interrupted or the verifier was already consumed.'
    );
  }

  const tokenEndpoint = `https://${config.authDomain}/oauth2/token`;
  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: redirectUriOverride || config.redirectUri,
    code_verifier: verifier,
  });

  try {
    return await postToTokenEndpoint(tokenEndpoint, params);
  } finally {
    // Always clear the verifier — it is single-use
    clearVerifier();
  }
}

/**
 * Refresh an access token using a stored refresh token (OAuth2 Hosted UI path).
 *
 * Use this when tokens were obtained via the authorization_code + PKCE flow
 * (Cognito Hosted UI or Google login). NOT compatible with SDK-issued tokens.
 *
 * @param {string} refreshToken - The refresh token from a previous PKCE exchange
 * @returns {Promise<{access_token: string, id_token: string, refresh_token: string}>}
 * @throws {Error} when the token endpoint returns an error or network fails
 */
export async function refreshTokens(refreshToken) {
  const config = getConfig();
  const tokenEndpoint = `https://${config.authDomain}/oauth2/token`;
  const params = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: config.clientId,
    refresh_token: refreshToken,
  });

  return postToTokenEndpoint(tokenEndpoint, params);
}

/**
 * Refresh an access token via the Cognito SDK API (REFRESH_TOKEN_AUTH flow).
 *
 * Use this when tokens were obtained via the SDK auth path (email/password or
 * magic link via auth-ui). The oauth2/token hosted-UI endpoint rejects SDK-
 * issued refresh tokens; the Cognito service endpoint accepts them.
 *
 * Cognito does NOT return a new refresh_token in REFRESH_TOKEN_AUTH responses —
 * the original refresh_token remains valid and is echoed back in the result.
 *
 * @param {string} refreshToken - The SDK refresh token from the previous auth
 * @returns {Promise<{access_token: string, id_token: string, refresh_token: string}>}
 * @throws {Error} when the Cognito API returns an error or network fails
 */
export async function refreshTokensViaCognitoApi(refreshToken) {
  const config = getConfig();

  // Extract AWS region from authDomain: "{prefix}.auth.{region}.amazoncognito.com"
  const regionMatch = config.authDomain.match(/\.auth\.(.+?)\.amazoncognito\.com/);
  if (!regionMatch) {
    throw new Error(`Cannot extract AWS region from authDomain: ${config.authDomain}`);
  }
  const region = regionMatch[1];
  const cognitoEndpoint = `https://cognito-idp.${region}.amazonaws.com/`;

  const response = await fetch(cognitoEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AmazonCognitoIdentityProvider.InitiateAuth',
    },
    body: JSON.stringify({
      AuthFlow: 'REFRESH_TOKEN_AUTH',
      AuthParameters: {
        REFRESH_TOKEN: refreshToken,
      },
      ClientId: config.clientId,
    }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      `Cognito refresh failed (${response.status}): ${data.message || data.__type || 'unknown error'}`
    );
  }

  const result = data.AuthenticationResult;
  return {
    access_token: result.AccessToken,
    id_token: result.IdToken,
    // Cognito does not issue a new refresh_token on refresh — keep the existing one
    refresh_token: refreshToken,
  };
}

/**
 * Call Cognito's GlobalSignOut endpoint to revoke all tokens for the user
 * across all devices and app clients.
 *
 * This is called on sign-out so that other apps (e.g. testapp) detect the
 * revocation on their next `visibilitychange` refresh attempt and sign the
 * user out locally.
 *
 * Fire-and-forget — never throws. Sign-out must not be blocked by a network
 * failure here; the local token clear and redirect always proceed regardless.
 *
 * @param {string} accessToken - The user's current access token
 * @returns {Promise<void>}
 */
export async function globalSignOut(accessToken) {
  const config = getConfig();
  const regionMatch = config.authDomain.match(/\.auth\.(.+?)\.amazoncognito\.com/);
  if (!regionMatch) {
    return;
  }
  const region = regionMatch[1];

  await fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': 'AmazonCognitoIdentityProvider.GlobalSignOut',
    },
    body: JSON.stringify({ AccessToken: accessToken }),
  }).catch(() => {});
}

/**
 * Build the logout URL.
 *
 * When `authUiUrl` is configured, routes through the auth-ui `/logout` page first.
 * The auth-ui clears its SSO session (fa_auth_ui_session) so that a subsequent
 * prompt=none attempt cannot silently re-authenticate the user, then forwards the
 * browser to Cognito's global logout endpoint to clear any hosted-UI session cookie.
 *
 * Without this two-step logout, a user who signs out would be silently re-authenticated
 * the next time they visit any Football Australia app (because the auth-ui session remains).
 *
 * @returns {string} Full logout URL
 */
export function buildLogoutUrl() {
  const config = getConfig();

  if (config.authUiUrl) {
    const params = new URLSearchParams({
      client_id: config.clientId,
      post_logout_redirect_uri: config.logoutUri,
    });
    return `${config.authUiUrl}/logout?${params.toString()}`;
  }

  // Fallback: direct Cognito logout (no auth-ui configured)
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.logoutUri,
  });
  return `https://${config.authDomain}/logout?${params.toString()}`;
}
